import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
import { useNespresso, NESPRESSO_TIERS, Tier } from '../context/NespressoContext';
import { useBrand } from '../../../context/BrandContext';
import Button from '../../../components/Button';

// Milestone type definition (generic, can be used by any gamification context)
export interface Milestone {
  tierLevel: number;
  tierName: string;
  badgeIcon?: string;
  reward?: {
    value: string;
    description: string;
  };
}

interface NespressoMilestoneCelebrationProps {
  tier?: Tier;
  onDismiss?: () => void;
}

const TIER_EMOJIS: Record<string, string> = {
  'first-sip': '\u2615',
  'espresso-explorer': '\u2615',
  'lungo-legend': '\u2615',
  'barista-bronze': '\ud83c\udfc6',
  'crema-connoisseur': '\u2b50',
  'master-roaster': '\u26a1',
  'grand-cru-guardian': '\ud83d\udc51',
};

/**
 * NespressoMilestoneCelebration - Celebration modal for tier achievements
 *
 * This component is designed to work with the NespressoContext gamification system.
 * It can receive tier data as props or automatically use context state.
 */
const NespressoMilestoneCelebration: React.FC<NespressoMilestoneCelebrationProps> = ({
  tier: propTier,
  onDismiss: propOnDismiss,
}) => {
  const { state, dispatch } = useNespresso();
  const { isDarkMode } = useBrand();

  // Get celebrating tier from state or prop
  const celebratingTier = propTier ?? (state.gamification.celebratingTierId
    ? NESPRESSO_TIERS.find(t => t.id === state.gamification.celebratingTierId)
    : null);

  const handleDismiss = () => {
    if (propOnDismiss) {
      propOnDismiss();
    } else {
      dispatch({ type: 'DISMISS_CELEBRATION' });
    }
  };

  if (!celebratingTier) {
    return null;
  }

  const badgeEmoji = TIER_EMOJIS[celebratingTier.id] || '\ud83c\udfc6';

  return (
    <AnimatePresence>
      {celebratingTier && (
        <motion.div
          className="fixed inset-0 z-[10000] flex items-center justify-center p-8"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3, ease: [0.25, 0.1, 0.25, 1] }}
        >
          {/* Background Overlay with blur */}
          <motion.div
            className="absolute inset-0 backdrop-blur-xl"
            style={{
              backgroundColor: isDarkMode
                ? 'rgba(0, 0, 0, 0.85)'
                : 'rgba(0, 0, 0, 0.75)',
            }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.25, 0.1, 0.25, 1] }}
            onClick={handleDismiss}
          />

          {/* Content Container */}
          <motion.div
            className="relative flex flex-col items-center max-w-[600px] w-full z-10"
            initial={{ scale: 0.8, opacity: 0, y: 40 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.9, opacity: 0, y: 20 }}
            transition={{
              type: 'spring',
              damping: 25,
              stiffness: 300,
              mass: 1,
            }}
          >
            {/* Confetti/Particles Effect - decorative circles */}
            <motion.div
              className="absolute inset-0 pointer-events-none overflow-hidden"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3 }}
            >
              {[...Array(12)].map((_, i) => (
                <motion.div
                  key={i}
                  className="absolute w-3 h-3 rounded-full"
                  style={{
                    background: i % 3 === 0
                      ? 'var(--brand-primary)'
                      : i % 3 === 1
                        ? 'var(--brand-accent)'
                        : 'var(--brand-secondary)',
                    left: `${10 + (i * 7) % 80}%`,
                    top: `${5 + (i * 11) % 30}%`,
                  }}
                  initial={{ scale: 0, opacity: 0 }}
                  animate={{
                    scale: [0, 1.2, 1],
                    opacity: [0, 1, 0.6],
                    y: [0, -20, -10],
                  }}
                  transition={{
                    delay: 0.4 + i * 0.05,
                    duration: 0.8,
                    ease: 'easeOut',
                  }}
                />
              ))}
            </motion.div>

            {/* Badge with spin-in animation */}
            <motion.div
              className="relative mb-8"
              initial={{ scale: 0, rotate: -180 }}
              animate={{ scale: 1, rotate: 0 }}
              transition={{
                type: 'spring',
                damping: 15,
                stiffness: 200,
                delay: 0.2,
              }}
            >
              {/* Glow effect behind badge */}
              <motion.div
                className="absolute inset-0 rounded-full blur-2xl"
                style={{
                  background: 'var(--brand-primary)',
                  transform: 'scale(1.5)',
                }}
                animate={{
                  opacity: [0.3, 0.6, 0.3],
                  scale: [1.3, 1.6, 1.3],
                }}
                transition={{
                  duration: 2,
                  repeat: Infinity,
                  ease: 'easeInOut',
                }}
              />

              {/* Badge circle */}
              <motion.div
                className="relative w-40 h-40 rounded-full flex items-center justify-center"
                style={{
                  background: `linear-gradient(135deg, var(--brand-primary) 0%, var(--brand-accent) 100%)`,
                  boxShadow: isDarkMode
                    ? '0 20px 60px rgba(0, 0, 0, 0.5), 0 0 0 4px rgba(255, 255, 255, 0.1)'
                    : '0 20px 60px rgba(0, 0, 0, 0.3), 0 0 0 4px rgba(255, 255, 255, 0.2)',
                }}
                animate={{
                  boxShadow: isDarkMode
                    ? [
                        '0 20px 60px rgba(0, 0, 0, 0.5), 0 0 0 4px rgba(255, 255, 255, 0.1)',
                        '0 25px 70px rgba(0, 0, 0, 0.6), 0 0 0 6px rgba(255, 255, 255, 0.15)',
                        '0 20px 60px rgba(0, 0, 0, 0.5), 0 0 0 4px rgba(255, 255, 255, 0.1)',
                      ]
                    : [
                        '0 20px 60px rgba(0, 0, 0, 0.3), 0 0 0 4px rgba(255, 255, 255, 0.2)',
                        '0 25px 70px rgba(0, 0, 0, 0.4), 0 0 0 6px rgba(255, 255, 255, 0.25)',
                        '0 20px 60px rgba(0, 0, 0, 0.3), 0 0 0 4px rgba(255, 255, 255, 0.2)',
                      ],
                }}
                transition={{
                  duration: 2,
                  repeat: Infinity,
                  ease: 'easeInOut',
                }}
              >
                <span className="text-7xl select-none" role="img" aria-label="tier badge">
                  {badgeEmoji}
                </span>
              </motion.div>
            </motion.div>

            {/* Congratulations Text */}
            <motion.div
              className="text-center mb-8"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4, duration: 0.5, ease: [0.25, 0.1, 0.25, 1] }}
            >
              <motion.p
                className="text-white/60 text-lg font-brand-secondary mb-2 uppercase tracking-widest"
                style={{ fontFamily: 'var(--brand-font-secondary)' }}
              >
                Congratulations!
              </motion.p>
              <motion.h1
                className="text-white text-5xl font-brand-bold mb-4"
                style={{ fontFamily: 'var(--brand-font-primary)' }}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.5, duration: 0.4 }}
              >
                {celebratingTier.name}
              </motion.h1>
              <motion.p
                className="text-white/50 text-xl"
                style={{ fontFamily: 'var(--brand-font-secondary)' }}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.6 }}
              >
                You've reached a new milestone!
              </motion.p>
            </motion.div>

            {/* Reward Info (if tier has reward) */}
            {celebratingTier.reward && (
              <motion.div
                className="w-full rounded-3xl p-8 mb-8 text-center"
                style={{
                  background: isDarkMode
                    ? 'rgba(255, 255, 255, 0.08)'
                    : 'rgba(255, 255, 255, 0.12)',
                  backdropFilter: 'blur(10px)',
                  border: isDarkMode
                    ? '1px solid rgba(255, 255, 255, 0.1)'
                    : '1px solid rgba(255, 255, 255, 0.2)',
                }}
                initial={{ opacity: 0, y: 20, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                transition={{ delay: 0.7, duration: 0.4 }}
              >
                <p
                  className="text-white/50 text-sm uppercase tracking-wider mb-2"
                  style={{ fontFamily: 'var(--brand-font-secondary)' }}
                >
                  Your Reward
                </p>
                <p
                  className="text-white text-3xl font-brand-bold mb-2"
                  style={{ fontFamily: 'var(--brand-font-primary)' }}
                >
                  {celebratingTier.reward.value || celebratingTier.reward.name}
                </p>
                <p
                  className="text-white/60 text-base"
                  style={{ fontFamily: 'var(--brand-font-secondary)' }}
                >
                  {celebratingTier.reward.description}
                </p>
              </motion.div>
            )}

            {/* Dismiss Button */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.8, duration: 0.4 }}
              className="w-full max-w-[320px]"
            >
              <Button
                variant="primary"
                size="large"
                onClick={handleDismiss}
                className="w-full"
              >
                Continue
              </Button>
            </motion.div>

            {/* Close button (X) in corner */}
            <motion.button
              className="absolute top-0 right-0 w-12 h-12 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 transition-colors"
              onClick={handleDismiss}
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.5 }}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              aria-label="Dismiss celebration"
            >
              <X size={24} stroke="white" strokeWidth={1.5} />
            </motion.button>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default NespressoMilestoneCelebration;
